/**
 * Clasificación de URL health en 4 estados visibles — ADR-009.
 *
 * El backend persiste `url_http_status` (código HTTP final, o 0 para
 * errores de red) y `url_last_checked_at` (timestamp del último check).
 * El frontend los interpreta para pintar un badge en la card del Radar.
 *
 * Política (del ADR-009):
 * - `unknown`  — nunca revisado (`url_last_checked_at === null`) o
 *                check > 7 días (stale). No asumimos salud.
 * - `ok`       — último status 2xx/3xx y revisado en las últimas 48h.
 * - `warn`     — 4xx (404/410) o stale entre 48h y 7 días.
 * - `broken`   — 5xx o error de red (status=0). El threshold de "3 días
 *                consecutivos" del ADR vive en el backend; el frontend
 *                solo pinta el último resultado.
 */

const HOURS = 60 * 60 * 1000;
const STALE_WARN_AFTER_MS = 48 * HOURS;
const STALE_UNKNOWN_AFTER_MS = 7 * 24 * HOURS;

export type UrlHealthStatus = "unknown" | "ok" | "warn" | "broken";

export interface UrlHealthMeta {
  status: UrlHealthStatus;
  label: string;
  /** Texto descriptivo para tooltip / aria-label. */
  tooltip: string;
}

export function classifyUrlHealth(
  httpStatus: number | null | undefined,
  lastCheckedAt: string | null | undefined,
  now: Date = new Date(),
): UrlHealthMeta {
  if (httpStatus == null || !lastCheckedAt) {
    return {
      status: "unknown",
      label: "Sin verificar",
      tooltip: "El link aún no fue verificado automáticamente. Puede o no estar activo.",
    };
  }

  const checkedAt = new Date(lastCheckedAt).getTime();
  const ageMs = now.getTime() - checkedAt;

  // Stale: check demasiado viejo, no confiamos en el resultado.
  if (ageMs > STALE_UNKNOWN_AFTER_MS) {
    return {
      status: "unknown",
      label: "Verificación pendiente",
      tooltip: `El último check fue hace más de 7 días. Puede haber cambiado.`,
    };
  }

  const httpBucket = Math.floor(httpStatus / 100);

  if (httpStatus === 0) {
    // Error de red / timeout (no HTTP). NO es `broken`: lo único que sabemos
    // es que *nuestro* servidor no llegó, y eso no es lo mismo que el enlace
    // esté roto. El check corre desde Railway (US) y el funcionario abre desde
    // Colombia; cuando un portal colombiano bloquea o desprioriza tráfico de
    // datacenters extranjeros, las dos respuestas difieren. Fue el caso real
    // de APC en septiembre de 2026 (#656): 7 convocatorias marcadas como
    // caídas cuyos enlaces abrían en 0.7s desde el municipio.
    //
    // Marcar como roto algo que funciona cuesta más que no decir nada: el
    // funcionario que abre dos "links inaccesibles" y los ve andar deja de
    // creerle al badge, y tampoco le va a creer el día que sí esté roto.
    return {
      status: "unknown",
      label: "No se pudo verificar",
      tooltip: "No pudimos conectarnos al portal para comprobarlo. Puedes intentar abrirlo.",
    };
  }

  if (httpBucket === 2 || httpBucket === 3) {
    if (ageMs > STALE_WARN_AFTER_MS) {
      return {
        status: "warn",
        label: "Verificación vieja",
        tooltip: "El último check fue hace más de 48 horas.",
      };
    }
    return {
      status: "ok",
      label: "Link activo",
      tooltip: "Verificado recientemente, el portal responde OK.",
    };
  }

  if (httpBucket === 4) {
    return {
      status: "warn",
      label: "Link puede fallar",
      tooltip: `El portal respondió HTTP ${httpStatus}. La página pudo haberse movido o cerrado.`,
    };
  }

  // 5xx
  return {
    status: "broken",
    label: "Link caído",
    tooltip: `El portal está respondiendo con error (HTTP ${httpStatus}).`,
  };
}
